/**
 * Privacy & Audit panel — customer-side surface for the privacy boundary.
 *
 *  - Pending grant requests (ops asked, customer decides)
 *  - Active grants (issued, time-boxed, revocable)
 *  - Recent ops access log (every read against this org's data)
 *
 * Lives inside OrgSettings.jsx (admin-only). The Tier 0/1/2 model is
 * spelled out in chargestack.ai/security; this panel is the lever.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function tierLabel(t) {
  return t === 2
    ? 'Tier 2 — content (case text, doc extracts, conversations)'
    : 'Tier 1 — names + filenames';
}

export default function PrivacyAuditPanel({ orgId }) {
  const [grants, setGrants] = useState([]);
  const [tier1, setTier1] = useState(null);   // { gating_on, qualifies, gated, onboarding, opted_visible }
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [issueDraft, setIssueDraft] = useState(null);  // for pending → respond
  const [showIssue, setShowIssue] = useState(false);
  const [issueForm, setIssueForm] = useState({
    granted_to_email: '',
    case_id: '',
    tier_access: 1,
    duration_hours: 1,
    reason: '',
  });

  const reload = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [g, e] = await Promise.all([
        api.get(`/orgs/${orgId}/grants`),
        api.get(`/orgs/${orgId}/access_log?limit=50`),
      ]);
      setGrants(g.grants || []);
      setTier1(g.tier1 || null);
      setEvents(e.events || []);
      setError(null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { reload(); }, [reload]);

  const pending = useMemo(() => grants.filter(g => g.status === 'pending'), [grants]);
  const active = useMemo(() => grants.filter(g => g.status === 'active' && g.is_currently_active), [grants]);
  const past = useMemo(() => grants.filter(g => !['pending', 'active'].includes(g.status) || !g.is_currently_active && g.status === 'active'), [grants]);

  async function handleIssueDirect(e) {
    e.preventDefault();
    if (!issueForm.granted_to_email || !issueForm.reason.trim()) return;
    try {
      await api.post(`/orgs/${orgId}/grants`, {
        granted_to_email: issueForm.granted_to_email,
        case_id: issueForm.case_id || null,
        tier_access: Number(issueForm.tier_access),
        duration_hours: Number(issueForm.duration_hours),
        reason: issueForm.reason,
      });
      setShowIssue(false);
      setIssueForm({ granted_to_email: '', case_id: '', tier_access: 1, duration_hours: 1, reason: '' });
      reload();
    } catch (err) { setError(err.message || String(err)); }
  }

  async function handleApprove(req, durationHours, reason) {
    try {
      await api.post(`/orgs/${orgId}/grants`, {
        grant_request_id: req.id,
        granted_to_email: req.granted_to_email,
        case_id: req.case_id,
        tier_access: req.tier_access,
        duration_hours: Number(durationHours),
        reason,
      });
      setIssueDraft(null);
      reload();
    } catch (err) { setError(err.message || String(err)); }
  }

  async function handleRevoke(grantId) {
    if (!window.confirm('Revoke this grant? Ops loses access immediately.')) return;
    try {
      await api.del(`/orgs/${orgId}/grants/${grantId}`);
      reload();
    } catch (err) { setError(err.message || String(err)); }
  }

  if (!orgId) return null;

  return (
    <div className="privacy-audit-panel">
      <div className="page-header">
        <h2>Privacy & Audit</h2>
        <button className="btn btn-secondary" onClick={() => setShowIssue(s => !s)}>
          {showIssue ? 'Cancel' : '+ Issue grant'}
        </button>
      </div>

      <p className="settings-detail" style={{ marginBottom: 16 }} data-testid="privacy-intro">
        ChargeStack ops cannot read your document text, AI output, messages
        or case descriptions (Tier 2) without an active grant you approve.{' '}
        {tier1?.gated
          ? 'Case names, numbers and filenames (Tier 1) are likewise visible to ops only under a grant you approve.'
          : tier1?.gating_on && tier1?.onboarding
            ? 'Case names, numbers and filenames (Tier 1) are visible to ops until your organization\'s first sign-in; after that they require a grant you approve.'
            : tier1?.gating_on && tier1?.opted_visible
              ? 'Case names, numbers and filenames (Tier 1) are visible to ops by your organization\'s choice (privacy posture), with every read logged.'
              : 'Case names, numbers and filenames (Tier 1) are visible to ops, with every read logged.'}
        {' '}Every access — including metadata only — is logged below.
      </p>

      {error && <div className="settings-message error">{error}</div>}

      {showIssue && (
        <form onSubmit={handleIssueDirect} className="grant-issue-form">
          <h3>Issue a new grant</h3>
          <div className="form-row">
            <label>Ops email</label>
            <input
              required
              type="email"
              value={issueForm.granted_to_email}
              onChange={e => setIssueForm(f => ({ ...f, granted_to_email: e.target.value }))}
              placeholder="ops@chargestack.ai"
            />
          </div>
          <div className="form-row">
            <label>Case ID (optional — leave blank for org-wide)</label>
            <input
              value={issueForm.case_id}
              onChange={e => setIssueForm(f => ({ ...f, case_id: e.target.value }))}
              placeholder="UUID"
            />
          </div>
          <div className="form-row">
            <label>Tier</label>
            <select
              value={issueForm.tier_access}
              onChange={e => setIssueForm(f => ({ ...f, tier_access: e.target.value }))}
            >
              <option value={1}>Tier 1 — names + filenames</option>
              <option value={2}>Tier 2 — content</option>
            </select>
          </div>
          <div className="form-row">
            <label>Duration (hours)</label>
            <input
              type="number"
              min={1}
              max={168}
              value={issueForm.duration_hours}
              onChange={e => setIssueForm(f => ({ ...f, duration_hours: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label>Reason</label>
            <textarea
              required
              rows={2}
              value={issueForm.reason}
              onChange={e => setIssueForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Why is this access needed?"
            />
          </div>
          <button type="submit" className="btn btn-primary">Issue grant</button>
        </form>
      )}

      <h3>Pending requests from ops ({pending.length})</h3>
      {pending.length === 0 ? (
        <p className="empty-state-small">No pending requests.</p>
      ) : (
        <table className="settings-table">
          <thead><tr><th>From</th><th>Tier</th><th>Case</th><th>Reason</th><th>Requested</th><th>Action</th></tr></thead>
          <tbody>
            {pending.map(g => (
              <tr key={g.id}>
                <td>{g.granted_to_email || g.granted_to_user_id}</td>
                <td>{tierLabel(g.tier_access)}</td>
                <td className="mono">{g.case_id ? g.case_id.slice(0, 8) : 'org-wide'}</td>
                <td style={{ maxWidth: 300 }}>{g.reason_request || '—'}</td>
                <td>{fmtDate(g.requested_at)}</td>
                <td>
                  <button className="btn btn-sm btn-primary" onClick={() => setIssueDraft(g)}>Approve</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleRevoke(g.id)}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {issueDraft && (
        <div className="modal-overlay" onClick={() => setIssueDraft(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Approve grant — {issueDraft.granted_to_email}</h3>
            <p>Tier: {tierLabel(issueDraft.tier_access)}</p>
            <p>Case: {issueDraft.case_id ? <span className="mono">{issueDraft.case_id}</span> : 'org-wide'}</p>
            <p>Reason from ops: {issueDraft.reason_request}</p>
            <ApproveForm
              onApprove={(d, r) => handleApprove(issueDraft, d, r)}
              onCancel={() => setIssueDraft(null)}
            />
          </div>
        </div>
      )}

      <h3>Active grants ({active.length})</h3>
      {active.length === 0 ? (
        <p className="empty-state-small">No active grants.</p>
      ) : (
        <table className="settings-table">
          <thead><tr><th>To</th><th>Tier</th><th>Case</th><th>Granted</th><th>Expires</th><th>Action</th></tr></thead>
          <tbody>
            {active.map(g => (
              <tr key={g.id}>
                <td>{g.granted_to_email}</td>
                <td>{tierLabel(g.tier_access)}</td>
                <td className="mono">{g.case_id ? g.case_id.slice(0, 8) : 'org-wide'}</td>
                <td>{fmtDate(g.granted_at)}</td>
                <td>{fmtDate(g.expires_at)}</td>
                <td><button className="btn btn-sm btn-danger" onClick={() => handleRevoke(g.id)}>Revoke</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Self-service exports</h3>
      <p className="empty-state-small">
        Download a JSON file of your data — useful for support tickets,
        compliance audits, or your own records.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <a className="btn btn-sm btn-secondary"
           href={`/api/v1/orgs/${orgId}/export/audit-trail`}
           target="_blank" rel="noopener">
          Audit trail (last 90d)
        </a>
        <a className="btn btn-sm btn-secondary"
           href={`/api/v1/orgs/${orgId}/export/cost-detail`}
           target="_blank" rel="noopener">
          Cost detail (last 90d)
        </a>
      </div>

      <h3>Recent ops access ({events.length})</h3>
      {loading && <p>Loading…</p>}
      {events.length === 0 ? (
        <p className="empty-state-small">No ops access recorded yet.</p>
      ) : (
        <table className="settings-table">
          <thead><tr><th>When</th><th>Who</th><th>Tier</th><th>Tables</th><th>Path</th><th>Purpose</th></tr></thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.id}>
                <td>{fmtDate(ev.accessed_at)}</td>
                <td>{ev.ops_email || ev.ops_user_id}</td>
                <td>{ev.tier_max}</td>
                <td className="mono" style={{ fontSize: 11 }}>{(ev.tables_touched || []).join(', ') || '—'}</td>
                <td className="mono" style={{ fontSize: 11 }}>{ev.request_method} {ev.request_path}</td>
                <td>{ev.query_purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ApproveForm({ onApprove, onCancel }) {
  const [duration, setDuration] = useState(1);
  const [reason, setReason] = useState('');
  return (
    <form onSubmit={e => { e.preventDefault(); onApprove(duration, reason); }}>
      <div className="form-row">
        <label>Duration (hours)</label>
        <input type="number" min={1} max={168} value={duration} onChange={e => setDuration(e.target.value)} />
      </div>
      <div className="form-row">
        <label>Note (optional)</label>
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. linked to ticket #123" />
      </div>
      <button type="submit" className="btn btn-primary">Issue grant</button>
      <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
    </form>
  );
}
